import { brandsWithCustomModels, brandModelMapping } from "@/config/aspectRatio/brandModelMapping";
import modelAspectRatio from "@/config/aspectRatio/modelAspectRatio";
import type { AspectRatioOption, ParsedAspectRatio, AspectRatioDimensions, Dimensions } from "./types";

const DEFAULT_UI_DIMENSIONS = {
  WIDTH: 320,
  HEIGHT: 320,
} as const;

const normalizeBrandId = (id: string): string =>
  id
    .split("-")
    .map((part) => part.trim())
    .join("-")
    .replaceAll(/\s+/g, " ") // safe enough here (bounded use)
    .trim()
    .toLowerCase();

const getCustomModel = (normalizedBrandId: string, normalizedWorkflow: string): string | undefined => {
  const workflowMapping = brandModelMapping.flowBrandMapping[normalizedWorkflow];

  const brandSpecificModels = workflowMapping?.brandSpecificModels;
  if (!brandSpecificModels) return;

  for (const [model, brands] of Object.entries(brandSpecificModels)) {
    if (brands?.includes(normalizedBrandId)) {
      return model;
    }
  }
};

/**
 * Returns the model name for a given brand and workflow.
 * @param brandId - The brand identifier (may contain inconsistent spaces/hyphens/casing)
 * @param workflow - The workflow type (e.g., 'campaign', 'lifestyle')
 * @returns The model name as per mapping rules.
 */

export const getModel = (brandId: string, workflow: string): string => {
  const normalizedBrandId = normalizeBrandId(brandId);
  const normalizedWorkflow = workflow?.trim().toLowerCase();

  if (brandsWithCustomModels.includes(normalizedBrandId)) {
    const customModel = getCustomModel(normalizedBrandId, normalizedWorkflow);
    if (customModel) return customModel;
  }

  return brandModelMapping.defaultModel;
};

export const getAspectRatioOptionsFromModel = (modelName: string): AspectRatioOption[] => {
  const modelAspectRatios = modelAspectRatio[modelName];

  if (!modelAspectRatios) {
    return [];
  }

  return Object.entries(modelAspectRatios).map(([aspectRatio, dimensions]) => {
    const [width, height] = dimensions.split("x");

    return {
      id: `${width} x ${height} px (${aspectRatio})`,
      label: `${width} x ${height} px`,
      ariaLabel: `${width} by ${height} pixels, ${aspectRatio.replace(":", " to ")} aspect ratio`,
    };
  });
};

/**
 * Returns aspect ratio options for a given brand and workflow
 * @param {string} brandId - The brand identifier (e.g., 'chips', 'milka', 'cadbury-uk')
 * @param {string} workflow - The workflow type (e.g., 'asset', 'lifestyle', 'background-images', 'video')
 * @returns {AspectRatioOption[]} Array of aspect ratio objects with id, label, and ariaLabel
 */
export const getAspectRatio = (brandId: string, workflow: string): AspectRatioOption[] => {
  const modelName = getModel(brandId, workflow);
  return getAspectRatioOptionsFromModel(modelName);
};

/**
 * Returns aspect ratio options directly by model name
 * @param {string} modelName - The model name (e.g., 'imagen', 'nano_banana_pro', 'bria')
 * @returns {AspectRatioOption[]} Array of aspect ratio objects with id, label, and ariaLabel
 */
export const getAspectRatioByModelName = (modelName: string): AspectRatioOption[] => {
  return getAspectRatioOptionsFromModel(modelName);
};

/**
 * Returns the aspect ratio dimensions object for a given brand and workflow.
 * @param {string} brandId - The brand identifier
 * @param {string} workflow - The workflow type
 * @returns {AspectRatioDimensions} Aspect ratio mapping, e.g., { "1:1": "1024x1024", ... }
 */
export const getAspectRatioDimensions = (brandId: string, workflow: string): AspectRatioDimensions => {
  const modelName = getModel(brandId, workflow);
  const modelAspectRatios = modelAspectRatio[modelName];
  return modelAspectRatios ?? {};
};

/**
 * Extracts the ratio portion from a formatted string
 */
const extractRatioFromString = (aspectRatioString: string): string | null => {
  const ratioRegex = /\((\d+:\d+)\)/;
  const match = ratioRegex.exec(aspectRatioString);
  return match ? match[1] : null;
};

/**
 * Calculates width and height based on aspect ratio
 */
const calculateUIDimensions = (ratioWidth: number, ratioHeight: number): Dimensions => {
  const { WIDTH: TARGET_WIDTH, HEIGHT: TARGET_HEIGHT } = DEFAULT_UI_DIMENSIONS;

  if (ratioWidth > ratioHeight) {
    // Landscape: fix width, calculate height
    return {
      width: TARGET_WIDTH,
      height: Math.round((TARGET_WIDTH * ratioHeight) / ratioWidth),
    };
  }

  if (ratioWidth < ratioHeight) {
    // Portrait: fix height, calculate width
    return {
      width: Math.round((TARGET_HEIGHT * ratioWidth) / ratioHeight),
      height: TARGET_HEIGHT,
    };
  }

  // Square (1:1)
  return {
    width: TARGET_WIDTH,
    height: TARGET_HEIGHT,
  };
};

/**
 * Parses an aspect ratio string and calculates UI dimensions dynamically.
 * @param {string} aspectRatioString - Format: "896 x 1280 px (3:4)"
 * @returns {ParsedAspectRatio} Object containing width, height, and ratio
 * @example
 * parseAspectRatio("896 x 1280 px (3:4)")
 * Returns: { width: 240, height: 320, ratio: "3:4" }
 */
export const parseAspectRatio = (aspectRatioString: string): ParsedAspectRatio => {
  const ratio = extractRatioFromString(aspectRatioString);

  if (!ratio) {
    return {
      width: DEFAULT_UI_DIMENSIONS.WIDTH,
      height: DEFAULT_UI_DIMENSIONS.HEIGHT,
      ratio: null,
    };
  }

  const [ratioWidth, ratioHeight] = ratio.split(":").map(Number);

  const { width, height } = calculateUIDimensions(ratioWidth, ratioHeight);

  return {
    width,
    height,
    ratio,
  };
};

/**
 * Returns the full resolution string for a given aspect ratio.
 *
 * Looks up the resolution mapped to the provided `aspectRatio`
 * (e.g., "3:4") for the resolved model, then formats it as:
 * "width x height px (aspectRatio)".
 *
 * @param brandId - Brand identifier used to resolve the model
 * @param workflow - Workflow name used to resolve the model
 * @param aspectRatio - Aspect ratio string (e.g., "3:4")
 *
 * @returns Formatted resolution string
 *
 * @example
 * getFullResolutionForRatio("brandA", "workflowX", "3:4")
 * // "896 x 1280 px (3:4)"
 */
export const getFullResolutionForRatio = (brandId: string, workflow: string, aspectRatio: string) => {
  const modelName = getModel(brandId, workflow);
  const modelRatios = modelAspectRatio[modelName];
  const [width, height] = (modelRatios?.[aspectRatio] ?? "").split("x");
  return `${width} x ${height} px (${aspectRatio})`;
};

/**
 * Returns the raw resolution (e.g., "1024x1024") for a given brand, workflow, and aspect ratio.
 *
 * @param brandId - Brand identifier (e.g., "chips ahoy - us")
 * @param workflow - Workflow type (e.g., "campaign")
 * @param aspectRatio - Aspect ratio string (e.g., "1:1")
 *
 * @returns Resolution string (e.g., "1024x1024") or empty string if not found
 */
export const aspectRatioToDimensions = (brandId: string, workflow: string, aspectRatio: string): string => {
  if (!aspectRatio) {
    return "";
  }

  const modelName = getModel(brandId, workflow);
  const modelRatios = modelAspectRatio[modelName];

  return modelRatios?.[aspectRatio] ?? "";
};
